<template>
  <div class="container">
    <div class="form-header">
      <h1 class="title-screen">{{ adminText('adminAll.finance_info_b1a049', 'Finance info') }}</h1>
      <div v-if="userRole === 'OWNER'" class="edit" @click="toggleEditing()">
        <img src="/assets/edit-pen.svg" :alt="adminText('adminAll.edit_icon_d1541c', 'Edit icon')" class="edit-pen">
        <p class="edit-text">{{ adminText('adminAll.edit_17865b', 'EDIT') }}</p>
      </div>
    </div>
    <p class="finance-description">{{ adminText('adminAll.your_finance_info_is_applied_only_on_events_organized_by_y_675a06', 'Your finance info is applied only on events organized by you.') }}</p>
    <div class="inputs">
      <text-field
        :admin-context="isAdminLocalization"
        v-model.trim="form.legalName"
        :label="adminText('adminAll.organization_legal_name_99d390', 'Organization legal name*')"
        :disabled="!enableEditing"
        :validator="$v.form.legalName"
      />
      <text-field
        :admin-context="isAdminLocalization"
        v-model.trim="form.OIB"
        :label="adminText('adminAll.oib_ba2e2b', 'OIB*')"
        :disabled="!enableEditing"
        :validator="$v.form.OIB"
      />
      <text-field
        :admin-context="isAdminLocalization"
        v-model.trim="form.authorizedPerson"
        :label="adminText('adminAll.name_and_surname_of_the_authorized_person_d3aa64', 'Name and surname of the authorized person')"
        :disabled="!enableEditing"
        :validator="$v.form.authorizedPerson"
      />
      <text-field
        :admin-context="isAdminLocalization"
        v-model.trim="form.address"
        :label="adminText('adminAll.address_28e798', 'Address*')"
        :disabled="!enableEditing"
        :validator="$v.form.address"
      />
      <div style="display: flex; gap: 1rem">
        <text-field
          :admin-context="isAdminLocalization"
          v-model.trim="form.city"
          :label="adminText('adminAll.city_d1b6eb', 'City*')"
          :disabled="!enableEditing"
          :validator="$v.form.city"
        />
        <text-field
          :admin-context="isAdminLocalization"
          v-model.trim="form.postalCode"
          :label="adminText('adminAll.postal_code_92caa7', 'Postal code*')"
          :disabled="!enableEditing"
          :validator="$v.form.postalCode"
        />
      </div>
      <text-field
        :admin-context="isAdminLocalization"
        v-model.trim="form.IBAN"
        :label="adminText('adminAll.iban_e81566', 'IBAN')"
        :disabled="!enableEditing"
        :validator="$v.form.IBAN"
      />
      <text-field
        :admin-context="isAdminLocalization"
        v-model.trim="form.primaryEmail"
        :label="adminText('adminAll.organizer_primary_email_1a2d07', 'Organizer primary email')"
        :disabled="!enableEditing"
        :validator="$v.form.primaryEmail"
      />
      <text-field
        :admin-context="isAdminLocalization"
        v-model.trim="form.contactNumber"
        :label="adminText('adminAll.phone_number_8961d3', 'Phone number')"
        :disabled="!enableEditing"
        :validator="$v.form.contactNumber"
      />
      <div class="radio-buttons">
        <p class="vat-text">{{ adminText('adminAll.u_sustavu_pdv_a_db97ce', 'U sustavu PDV-a') }}</p>
        <div class="radio-item">
          <input
            id="no"
            v-model="form.VATObligor"
            type="radio"
            :validator="$v.form.VATObligor"
            :value="false"
            :disabled="!enableEditing"
          >
          <label for="no">{{ adminText('adminAll.ne_f736bf', 'Ne') }}</label>
        </div>
        <div class="radio-item">
          <input
            id="yes"
            v-model="form.VATObligor"
            :validator="$v.form.VATObligor"
            type="radio"
            :value="true"
            :disabled="!enableEditing"
          >
          <label for="yes">{{ adminText('adminAll.da_c1583c', 'Da') }}</label>
        </div>
      </div>
      <div class="checkbox-row">
        <input
          id="e-invoice"
          v-model="form.EInvoiceEnabled"
          type="checkbox"
          :disabled="!enableEditing || form.VATObligor"
        >
        <label for="e-invoice">{{ adminText('adminAll.posalji_e_racun_i_ako_niste_u_sustavu_pdv_a_aa6b26', 'Pošalji e-račun i ako niste u sustavu PDV-a') }}</label>
      </div>
    </div>
    <main-button
      :text="adminText('adminAll.save_efc007', 'Save')"
      depressed
      class="button"
      :disabled="!enableEditing || organizationHasActiveEvents"
      @click="submitForm"
    />
    <p v-if="enableEditing && organizationHasActiveEvents">
      {{ adminText('adminAll.this_is_sensitive_info_that_is_subject_of_fiscalisation_an_071a26', 'This is sensitive info that is subject of fiscalisation and cannot be change when you have active sales for events. Please contact CoreEvent to make changes.') }}
    </p>
    <simple-snackbar v-bind.sync="snackbar" :message-prop="adminAllMessage(snackbar.messageProp)" />
  </div>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import AdminContext from '@/admin-i18n/context'
import { email, required } from 'vuelidate/lib/validators'
import { omit } from 'lodash'
import moment from 'moment'

import { GET_ORGANIZATION, UPDATE_ORGANIZATION } from '../../graphql/organization.graphql'
import { submitGuard } from './../../utils'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'

export default {
  mixins: [AdminContext, AdminAllMessages],
  name: 'finance-info',
  components: { SimpleSnackbar },
  props: {
    initialValues: {
      type: Object,
      default: () => {},
    },
    vs: {
      type: Object,
      default: () => {},
    },
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      enableEditing: false,
      snackbar: {},
      form: {},
      userRole: null,
      organizationHasActiveEvents: false,
    }
  },
  validations() {
    return {
      form: {
        legalName: { required },
        OIB: { required },
        authorizedPerson: { required },
        IBAN: { required },
        address: { required },
        city: { required },
        postalCode: { required },
        VATObligor: { required },
        EInvoiceEnabled: {},
        primaryEmail: { required, email },
        contactNumber: { required },
      },
    }
  },
  watch: {
    'form.VATObligor': {
      handler(isVatObligor) {
        if (isVatObligor) {
          this.form.EInvoiceEnabled = true
        }
      },
    },
  },
  methods: {
    toggleEditing() {
      this.enableEditing = !this.enableEditing
    },
    async submitForm() {
      if (this.$v.$invalid) {
        this.snackbar = {
          open: true,
          messageProp: this.adminAllNotice('Fill in the required information.'),
          color: '#DB384C',
        }
      }
      submitGuard(this, async () => {
        const organizationInfo = this.form
        if (organizationInfo.VATObligor) {
          organizationInfo.EInvoiceEnabled = true
        }
        organizationInfo.VATPercentage = this.form.VATObligor ? 5.0 : 0.0

        const { errors } = await this.$apollo.mutate({
          mutation: UPDATE_ORGANIZATION,
          variables: {
            organizationId: this.form.id,
            organizationInfo: omit(organizationInfo, ['id', '__typename']),
          },
          refetchQueries: [
            { query: GET_ORGANIZATION, variables: { organizationSlug: this.organizationSlug } },
          ],
        })

        this.snackbar = {
          open: true,
          messageProp: errors ? this.adminAllNotice('Something went wrong. Please try again.') : this.adminAllNotice('Finance info saved.'),
          color: errors ? 'error' : 'primary',
        }
        this.toggleEditing()
      })
    },
  },
  apollo: {
    getOrganization: {
      query: GET_ORGANIZATION,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data, loading }) {
        if (!loading && data) {
          const { events, ...rest } = data.getOrganization
          this.organizationHasActiveEvents = events.some(e => moment(e.to).isAfter(moment()))
          this.userRole = rest.userOrganization.role
          this.form = omit(rest, ['liveEventsCount', 'userOrganization', 'organizationRole'])
          if (this.form.VATObligor) {
            this.form.EInvoiceEnabled = true
          }
        }
      },
    },
  },
}
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  padding: 0 !important;
  margin: 0 !important;
  max-width: 43.75rem;
}

.organizer-name {
  display: flex;
  flex-wrap: wrap;
  column-gap: 1.5rem;
}

.organizer-info {
  display: flex;
  flex-wrap: wrap;
  column-gap: 1.5rem;
}

.links {
  display: flex;
  column-gap: 1.5rem;
  flex-wrap: wrap;
}

.button {
  width: 6.25rem;
  align-self: flex-end;
}

.vat-text {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1.125rem;
  color: #333333;
}

.radio-item {
  display: inline-block;
  position: relative;
  padding: 0 0.375rem;
  margin: 0.625rem 0 0;
}

.radio-item input[type='radio'] {
  display: none;
}

.radio-item label {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1.125rem;
  color: #979797;
}

.radio-item label:before {
  content: ' ';
  display: inline-block;
  position: relative;
  top: 0.313rem;
  margin: 0 0.313rem 0 0;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.688rem;
  border: 0.125rem solid #666666;
  background-color: transparent;
}

.radio-item input[type='radio']:checked + label:after {
  border-radius: 0.688rem;
  width: 0.75rem;
  height: 0.75rem;
  position: absolute;
  top: 0.563rem;
  left: 0.625rem;
  content: ' ';
  display: block;
  background: #0277bd;
}

.form-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem;
  color: #ff4c00;
}

.finance-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
}

.edit-text {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 1.125rem;
  color: #0277bd;
  margin-bottom: 0;
}

.edit {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.edit-pen {
  height: 1.125rem;
  width: 1.125rem;
  margin-right: 0.625rem;
}

.form-header {
  display: flex;
  justify-content: space-between;
}

.inputs {
  display: flex;
  flex-direction: column;
  row-gap: 2rem;
}

.finance-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  color: #979797;
}
.checkbox-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Source Sans Pro';
  font-size: 1rem;
  color: #333333;
}
</style>
